import React, { useEffect, useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonChip,
  IonCol,
  IonGrid,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonProgressBar,
  IonRow,
  IonText,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { checkmarkCircle, warningOutline, alertCircle, refreshOutline, chevronDown, chevronUp, stopCircleOutline } from 'ionicons/icons';
import Content from '../components/Content';
import { usePageTitle } from '../util/sugar';
import {
  accessibilityService,
  AccessibilityReport,
  AccessibilityViolation,
  TESTABLE_ROUTES,
} from '../services/AccessibilityService';

const ViolationItem: React.FC<{ violation: AccessibilityViolation; getImpactBadge: (impact: string) => string }> = ({ violation, getImpactBadge }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: '8px' }}>
      <IonItem
        button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${violation.impact} issue: ${violation.id}. ${violation.help}. Click to ${expanded ? 'collapse' : 'expand'} details.`}
      >
        <IonChip color={getImpactBadge(violation.impact)} slot="start">
          {violation.impact}
        </IonChip>
        <IonLabel>
          <h3>{violation.id}</h3>
          <p>{violation.help}</p>
        </IonLabel>
        <IonIcon
          icon={expanded ? chevronUp : chevronDown}
          slot="end"
          aria-hidden="true"
        />
      </IonItem>
      {expanded && (
        <div style={{ padding: '16px', background: 'var(--ion-color-light)', borderRadius: '0 0 8px 8px' }}>
          <p><strong>Description:</strong> {violation.description}</p>
          <p>
            <a href={violation.helpUrl} target="_blank" rel="noopener noreferrer">
              Learn more about this issue
            </a>
          </p>
          <h4>Affected Elements ({violation.nodes.length}):</h4>
          <IonList>
            {violation.nodes.slice(0, 5).map((node, nIndex) => (
              <IonItem key={nIndex}>
                <IonLabel className="ion-text-wrap">
                  <p><strong>Element:</strong></p>
                  <code style={{
                    display: 'block',
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto',
                  }}>
                    {node.html}
                  </code>
                  <p><strong>Fix:</strong> {node.failureSummary}</p>
                </IonLabel>
              </IonItem>
            ))}
            {violation.nodes.length > 5 && (
              <IonItem>
                <IonLabel>
                  ...and {violation.nodes.length - 5} more elements
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        </div>
      )}
    </div>
  );
};

const Accessibility: React.FC = () => {
  usePageTitle('Accessibility Report');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState<'current' | 'full'>('current');
  const [currentPage, setCurrentPage] = useState('');
  const [report, setReport] = useState<AccessibilityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSavedResults, setHasSavedResults] = useState(false);

  // Check for saved results on mount
  useEffect(() => {
    const savedResults = accessibilityService.getSavedResults();
    if (savedResults.length > 0) {
      setHasSavedResults(true);
      // Show the saved report
      const savedReport = accessibilityService.getMultiPageReport();
      if (savedReport) {
        setReport(savedReport);
        setScanMode('full');
      }
    } else {
      // No saved results, run current page scan
      runCurrentPageScan();
    }
  }, []);

  const runCurrentPageScan = async () => {
    setLoading(true);
    setScanning(true);
    setError(null);

    try {
      setCurrentPage('Current Page');
      const result = await accessibilityService.scanCurrentPage('Accessibility Report Page');
      const generatedReport = accessibilityService.generateReport([result]);
      setReport(generatedReport);
    } catch (err: any) {
      setError(err.message || 'Failed to run accessibility scan');
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const runFullSiteScan = () => {
    // Start the multi-page scan - this will navigate away from this page
    accessibilityService.startMultiPageScan(TESTABLE_ROUTES);
  };

  const clearSavedResults = () => {
    accessibilityService.clearSavedResults();
    setHasSavedResults(false);
    setReport(null);
    runCurrentPageScan();
  };

  const runScan = () => {
    if (scanMode === 'full') {
      runFullSiteScan();
    } else {
      runCurrentPageScan();
    }
  };

  const getScoreIcon = (score: number) => {
    if (score >= 90) return checkmarkCircle;
    if (score >= 70) return warningOutline;
    return alertCircle;
  };

  const getImpactBadge = (impact: string) => {
    const colors: Record<string, string> = {
      critical: 'danger',
      serious: 'warning',
      moderate: 'secondary',
      minor: 'medium',
    };
    return colors[impact] || 'medium';
  };

  return (
    <Content id="main">
      <IonGrid>
        <IonRow>
          <IonCol sizeMd="10" sizeLg="8" offsetMd="1" offsetLg="2">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>
                  <h1>WCAG 2.1 AA Accessibility Report</h1>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p>
                  This report checks compliance with Web Content Accessibility Guidelines (WCAG) 2.1 Level AA,
                  which is the standard required for University of Michigan digital accessibility compliance.
                </p>

                <div style={{ margin: '16px 0' }}>
                  <IonSegment
                    value={scanMode}
                    onIonChange={(e) => setScanMode(e.detail.value as 'current' | 'full')}
                  >
                    <IonSegmentButton value="current">
                      <IonLabel>Current Page</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="full">
                      <IonLabel>Full Site Scan ({TESTABLE_ROUTES.length} pages)</IonLabel>
                    </IonSegmentButton>
                  </IonSegment>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <IonButton
                    onClick={runScan}
                    disabled={scanning}
                    aria-label={scanning ? 'Scanning in progress' : `Run ${scanMode === 'full' ? 'full site' : 'current page'} accessibility scan`}
                  >
                    <IonIcon slot="start" icon={refreshOutline} aria-hidden="true" />
                    {scanning ? 'Scanning...' : scanMode === 'full' ? 'Scan All Pages' : 'Scan Current Page'}
                  </IonButton>

                  {hasSavedResults && (
                    <IonButton
                      color="medium"
                      onClick={clearSavedResults}
                      aria-label="Clear saved scan results"
                    >
                      <IonIcon slot="start" icon={stopCircleOutline} aria-hidden="true" />
                      Clear Results
                    </IonButton>
                  )}
                </div>

                {scanMode === 'full' && (
                  <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--ion-color-medium)' }}>
                    Full site scan will navigate through each page to test: {TESTABLE_ROUTES.map(r => r.name).join(', ')}.
                    Results are saved and displayed when you return to this page.
                  </p>
                )}
              </IonCardContent>
            </IonCard>

            <IonLoading
              isOpen={loading}
              message={`Scanning: ${currentPage}`}
            />

            {error && (
              <IonCard color="danger">
                <IonCardContent>
                  <IonText color="light">
                    <p>{error}</p>
                    <p>Make sure axe-core is installed: <code>yarn add axe-core</code></p>
                  </IonText>
                </IonCardContent>
              </IonCard>
            )}

            {report && (
              <>
                {/* Overall Score Card */}
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle>Overall Score</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <IonIcon
                        icon={getScoreIcon(report.overallScore)}
                        style={{
                          fontSize: '64px',
                          color: accessibilityService.getScoreColor(report.overallScore),
                        }}
                        aria-hidden="true"
                      />
                      <h2
                        style={{
                          fontSize: '48px',
                          margin: '10px 0',
                          color: accessibilityService.getScoreColor(report.overallScore),
                        }}
                        aria-label={`Overall accessibility score: ${report.overallScore} percent`}
                      >
                        {report.overallScore}%
                      </h2>
                      <IonProgressBar
                        value={report.overallScore / 100}
                        color={report.overallScore >= 90 ? 'success' : report.overallScore >= 70 ? 'warning' : 'danger'}
                        aria-label={`Accessibility score progress: ${report.overallScore}%`}
                      />
                    </div>

                    <IonGrid>
                      <IonRow>
                        <IonCol>
                          <IonChip color="danger">
                            Critical: {report.criticalCount}
                          </IonChip>
                        </IonCol>
                        <IonCol>
                          <IonChip color="warning">
                            Serious: {report.seriousCount}
                          </IonChip>
                        </IonCol>
                        <IonCol>
                          <IonChip color="secondary">
                            Moderate: {report.moderateCount}
                          </IonChip>
                        </IonCol>
                        <IonCol>
                          <IonChip color="medium">
                            Minor: {report.minorCount}
                          </IonChip>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                    <p style={{ textAlign: 'center', marginTop: '10px' }}>
                      {report.pagesScanned > 1 && `${report.pagesScanned} pages scanned • `}
                      Generated: {report.generatedAt.toLocaleString()}
                    </p>
                  </IonCardContent>
                </IonCard>

                {/* Violations Detail */}
                {report.pageResults.map((pageResult, pageIndex) => (
                  <IonCard key={pageIndex}>
                    <IonCardHeader>
                      <IonCardTitle>
                        {pageResult.name} ({pageResult.url}) - Score: {pageResult.score}%
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      {pageResult.violations.length === 0 ? (
                        <IonItem>
                          <IonIcon icon={checkmarkCircle} color="success" slot="start" aria-hidden="true" />
                          <IonLabel>No accessibility violations found!</IonLabel>
                        </IonItem>
                      ) : (
                        <div role="list" aria-label={`Accessibility violations for ${pageResult.name}`}>
                          {pageResult.violations.map((violation, vIndex) => (
                            <ViolationItem
                              key={vIndex}
                              violation={violation}
                              getImpactBadge={getImpactBadge}
                            />
                          ))}
                        </div>
                      )}
                    </IonCardContent>
                  </IonCard>
                ))}

                {/* WCAG Reference */}
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle>WCAG 2.1 AA Criteria Tested</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonList>
                      <IonItem>
                        <IonLabel>
                          <h3>1.1.1 Non-text Content</h3>
                          <p>All images have appropriate alt text</p>
                        </IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          <h3>1.4.3 Contrast (Minimum)</h3>
                          <p>Text has sufficient color contrast (4.5:1 ratio)</p>
                        </IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          <h3>2.1.1 Keyboard</h3>
                          <p>All functionality is accessible via keyboard</p>
                        </IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          <h3>2.4.3 Focus Order</h3>
                          <p>Focus order is logical and sequential</p>
                        </IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonLabel>
                          <h3>4.1.2 Name, Role, Value</h3>
                          <p>UI components have accessible names and roles</p>
                        </IonLabel>
                      </IonItem>
                    </IonList>
                  </IonCardContent>
                </IonCard>
              </>
            )}
          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default Accessibility;
